/**
 * i18n maison pour catho-paroisses (web).
 *
 * Mêmes conventions que le système mobile cathoplus :
 *  - Fonction `t(key, params?)` via `useTranslation()`
 *  - Clés en dot-notation (`module.section.libelle`)
 *  - Interpolation `{{var}}` via params
 *  - 2 langues : fr (défaut) + en
 *  - Persistance localStorage
 *
 * Adapté pour le web :
 *  - localStorage au lieu d'AsyncStorage
 *  - synchrone (pas besoin de Promise sur le storage)
 *
 * --------------------------------------------------------------------------
 * CONTRAT DE SÉCURITÉ XSS
 * --------------------------------------------------------------------------
 * Le retour de `t()` est destiné à être rendu en TEXT NODE React
 * (`{t("key")}`), qui auto-échappe. Ne JAMAIS le passer à
 * `dangerouslySetInnerHTML` ni `.innerHTML` — un test grep verrouille
 * cette règle (`client/src/__tests__/security/no-t-in-dangerously-set.test.ts`).
 *
 * --------------------------------------------------------------------------
 * RÈGLE — CLÉS RÉFÉRENCÉES DYNAMIQUEMENT (préservation à la purge)
 * --------------------------------------------------------------------------
 * Certaines clés sont consommées via une variable plutôt qu'une string
 * littérale, par exemple dans `app-sidebar.tsx` :
 *
 *     const NAV_GROUPS = [
 *       { labelKey: "sidebarGroups.paroissiens", ... },
 *       { labelKey: "sidebarGroups.finances", ... },
 *       ...
 *     ];
 *     // Plus loin : t(group.labelKey)
 *
 * Un grep statique de `t("...")` NE LES DÉTECTERA PAS. Toute purge
 * automatique de clés "orphelines" doit explicitement préserver les
 * sous-arbres ci-dessous :
 *
 *   - `common.sidebarGroups.*`      (app-sidebar.tsx → NAV_GROUPS_CONFIG[].labelKey,
 *                                    notez le PRÉFIXE namespace `common.` —
 *                                    les clés JSON sont sous `common`)
 *   - `analytics.predict.*`          (références dynamiques par jour/risque)
 *   - `paroissiens.profil.tabs.*`    (boucle sur tabs)
 *   - `sacrements.preparation.page.exams.status.*`
 *   - `cimetiere.stats.*`            (stats.map => t(`cimetiere.stats.${k}`))
 *   - `auth.roles.*`                 (mapping de rôle dynamique)
 *   - `dashboard.main.activityDetail.statuts.*` (statut d'un don, fiche du
 *                                    fil « Activité en direct »)
 *   - `dashboard.attend.items.*` et `dashboard.attend.urgences.*`
 *                                    (bloc « Ce qui vous attend », clé
 *                                    choisie par dashboard-parts/attend.ts)
 *   - `common.assistance.services.*` (pages/assistance.tsx → état des
 *                                    services, clé renvoyée par le BFF)
 *   - `notifications.domaines.*`     (pages/notifications.tsx → domaine
 *                                    d'une notification)
 *   - `finance.guichet.menu`         (lib/menu-sections.ts → libellé de
 *                                    l'entrée « Encaissements au guichet »,
 *                                    page 91)
 *   - toute clé `*_feminin` / `*_masculin` (accord en genre, lue par le
 *                                    paramètre `context` de `t()`, voir
 *                                    lib/accord-genre.ts)
 *
 * Pour ajouter une nouvelle clé dynamique : préfère un commentaire
 * `// i18n-dynamic: <prefix>` dans le fichier source ET un ajout à cette
 * liste ; sinon la prochaine purge la flagguera comme morte.
 * --------------------------------------------------------------------------
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";

import frCommon from "../../i18n/locales/fr/common.json";
import enCommon from "../../i18n/locales/en/common.json";
import frAuth from "../../i18n/locales/fr/auth.json";
import enAuth from "../../i18n/locales/en/auth.json";
import frDashboard from "../../i18n/locales/fr/dashboard.json";
import enDashboard from "../../i18n/locales/en/dashboard.json";
import frParoissiens from "../../i18n/locales/fr/paroissiens.json";
import enParoissiens from "../../i18n/locales/en/paroissiens.json";
import frSacrements from "../../i18n/locales/fr/sacrements.json";
import enSacrements from "../../i18n/locales/en/sacrements.json";
import frFinance from "../../i18n/locales/fr/finance.json";
import enFinance from "../../i18n/locales/en/finance.json";
import frCatechese from "../../i18n/locales/fr/catechese.json";
import enCatechese from "../../i18n/locales/en/catechese.json";
import frEvenements from "../../i18n/locales/fr/evenements.json";
import enEvenements from "../../i18n/locales/en/evenements.json";
import frRh from "../../i18n/locales/fr/rh.json";
import enRh from "../../i18n/locales/en/rh.json";
import frPastoral from "../../i18n/locales/fr/pastoral.json";
import enPastoral from "../../i18n/locales/en/pastoral.json";
import frCommunication from "../../i18n/locales/fr/communication.json";
import enCommunication from "../../i18n/locales/en/communication.json";
import frCommunity from "../../i18n/locales/fr/community.json";
import enCommunity from "../../i18n/locales/en/community.json";
import frInfra from "../../i18n/locales/fr/infra.json";
import enInfra from "../../i18n/locales/en/infra.json";
import frSecretariat from "../../i18n/locales/fr/secretariat.json";
import enSecretariat from "../../i18n/locales/en/secretariat.json";
import frAnalytics from "../../i18n/locales/fr/analytics.json";
import enAnalytics from "../../i18n/locales/en/analytics.json";
import frCms from "../../i18n/locales/fr/cms.json";
import enCms from "../../i18n/locales/en/cms.json";
import frEcommerce from "../../i18n/locales/fr/ecommerce.json";
import enEcommerce from "../../i18n/locales/en/ecommerce.json";
import frInnovation from "../../i18n/locales/fr/innovation.json";
import enInnovation from "../../i18n/locales/en/innovation.json";
import frDiocese from "../../i18n/locales/fr/diocese.json";
import enDiocese from "../../i18n/locales/en/diocese.json";
import frAdministration from "../../i18n/locales/fr/administration.json";
import enAdministration from "../../i18n/locales/en/administration.json";
import frArchives from "../../i18n/locales/fr/archives.json";
import enArchives from "../../i18n/locales/en/archives.json";
import frBatiment from "../../i18n/locales/fr/batiment.json";
import enBatiment from "../../i18n/locales/en/batiment.json";
import frCimetiere from "../../i18n/locales/fr/cimetiere.json";
import enCimetiere from "../../i18n/locales/en/cimetiere.json";
import frCourrier from "../../i18n/locales/fr/courrier.json";
import enCourrier from "../../i18n/locales/en/courrier.json";
import frCrises from "../../i18n/locales/fr/crises.json";
import enCrises from "../../i18n/locales/en/crises.json";
import frDomotique from "../../i18n/locales/fr/domotique.json";
import enDomotique from "../../i18n/locales/en/domotique.json";
import frEnergie from "../../i18n/locales/fr/energie.json";
import enEnergie from "../../i18n/locales/en/energie.json";
import frFeedback from "../../i18n/locales/fr/feedback.json";
import enFeedback from "../../i18n/locales/en/feedback.json";
import frImpressions from "../../i18n/locales/fr/impressions.json";
import enImpressions from "../../i18n/locales/en/impressions.json";
import frInventaire from "../../i18n/locales/fr/inventaire.json";
import enInventaire from "../../i18n/locales/en/inventaire.json";
import frInventaireDetail from "../../i18n/locales/fr/inventaireDetail.json";
import enInventaireDetail from "../../i18n/locales/en/inventaireDetail.json";
import frJardins from "../../i18n/locales/fr/jardins.json";
import enJardins from "../../i18n/locales/en/jardins.json";
import frLogistique from "../../i18n/locales/fr/logistique.json";
import enLogistique from "../../i18n/locales/en/logistique.json";
import frMedia from "../../i18n/locales/fr/media.json";
import enMedia from "../../i18n/locales/en/media.json";
import frNotifications from "../../i18n/locales/fr/notifications.json";
import enNotifications from "../../i18n/locales/en/notifications.json";
import frParametres from "../../i18n/locales/fr/parametres.json";
import enParametres from "../../i18n/locales/en/parametres.json";
import frPatrimoine from "../../i18n/locales/fr/patrimoine.json";
import enPatrimoine from "../../i18n/locales/en/patrimoine.json";
import frReseaux from "../../i18n/locales/fr/reseaux.json";
import enReseaux from "../../i18n/locales/en/reseaux.json";
import frSecurite from "../../i18n/locales/fr/securite.json";
import enSecurite from "../../i18n/locales/en/securite.json";
import frStocks from "../../i18n/locales/fr/stocks.json";
import enStocks from "../../i18n/locales/en/stocks.json";
import frUrgences from "../../i18n/locales/fr/urgences.json";
import enUrgences from "../../i18n/locales/en/urgences.json";
import frVehicules from "../../i18n/locales/fr/vehicules.json";
import enVehicules from "../../i18n/locales/en/vehicules.json";

export type Locale = "fr" | "en";

interface I18nContextValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
  isReady: boolean;
}

const LOCALE_STORAGE_KEY = "paroisses_locale";
const DEFAULT_LOCALE: Locale = "fr";
const SUPPORTED_LOCALES: Locale[] = ["fr", "en"];

export const LOCALE_NAMES: Record<Locale, string> = {
  fr: "Français",
  en: "English",
};

type TranslationDict = Record<string, unknown>;

function mergeNamespaces(modules: Array<[string, unknown]>): TranslationDict {
  const out: TranslationDict = {};
  for (const [ns, dict] of modules) out[ns] = dict;
  return out;
}

const TRANSLATIONS: Record<Locale, TranslationDict> = {
  fr: mergeNamespaces([
    ["common", frCommon],
    ["auth", frAuth],
    ["dashboard", frDashboard],
    ["paroissiens", frParoissiens],
    ["sacrements", frSacrements],
    ["finance", frFinance],
    ["catechese", frCatechese],
    ["evenements", frEvenements],
    ["rh", frRh],
    ["pastoral", frPastoral],
    ["communication", frCommunication],
    ["community", frCommunity],
    ["infra", frInfra],
    ["secretariat", frSecretariat],
    ["analytics", frAnalytics],
    ["cms", frCms],
    ["ecommerce", frEcommerce],
    ["innovation", frInnovation],
    ["diocese", frDiocese],
    ["administration", frAdministration],
    ["archives", frArchives],
    ["batiment", frBatiment],
    ["cimetiere", frCimetiere],
    ["courrier", frCourrier],
    ["crises", frCrises],
    ["domotique", frDomotique],
    ["energie", frEnergie],
    ["feedback", frFeedback],
    ["impressions", frImpressions],
    ["inventaire", frInventaire],
    ["inventaireDetail", frInventaireDetail],
    ["jardins", frJardins],
    ["logistique", frLogistique],
    ["media", frMedia],
    ["notifications", frNotifications],
    ["parametres", frParametres],
    ["patrimoine", frPatrimoine],
    ["reseaux", frReseaux],
    ["securite", frSecurite],
    ["stocks", frStocks],
    ["urgences", frUrgences],
    ["vehicules", frVehicules],
  ]),
  en: mergeNamespaces([
    ["common", enCommon],
    ["auth", enAuth],
    ["dashboard", enDashboard],
    ["paroissiens", enParoissiens],
    ["sacrements", enSacrements],
    ["finance", enFinance],
    ["catechese", enCatechese],
    ["evenements", enEvenements],
    ["rh", enRh],
    ["pastoral", enPastoral],
    ["communication", enCommunication],
    ["community", enCommunity],
    ["infra", enInfra],
    ["secretariat", enSecretariat],
    ["analytics", enAnalytics],
    ["cms", enCms],
    ["ecommerce", enEcommerce],
    ["innovation", enInnovation],
    ["diocese", enDiocese],
    ["administration", enAdministration],
    ["archives", enArchives],
    ["batiment", enBatiment],
    ["cimetiere", enCimetiere],
    ["courrier", enCourrier],
    ["crises", enCrises],
    ["domotique", enDomotique],
    ["energie", enEnergie],
    ["feedback", enFeedback],
    ["impressions", enImpressions],
    ["inventaire", enInventaire],
    ["inventaireDetail", enInventaireDetail],
    ["jardins", enJardins],
    ["logistique", enLogistique],
    ["media", enMedia],
    ["notifications", enNotifications],
    ["parametres", enParametres],
    ["patrimoine", enPatrimoine],
    ["reseaux", enReseaux],
    ["securite", enSecurite],
    ["stocks", enStocks],
    ["urgences", enUrgences],
    ["vehicules", enVehicules],
  ]),
};

function getNestedValue(obj: TranslationDict, path: string): string | undefined {
  const keys = path.split(".");
  let cur: unknown = obj;
  for (const k of keys) {
    if (cur && typeof cur === "object" && k in (cur as Record<string, unknown>)) {
      cur = (cur as Record<string, unknown>)[k];
    } else {
      return undefined;
    }
  }
  return typeof cur === "string" ? cur : undefined;
}

/**
 * Pluriels, convention i18next : `t("a.b", { count })` lit `a.b_one` ou
 * `a.b_other` selon les règles CLDR de la langue (Intl.PluralRules). En
 * français 0 et 1 sont au singulier (« 0 don », « 1 don », « 2 dons ») ; en
 * anglais seul 1 l'est. Une clé simple `a.b` reste servie telle quelle quand
 * aucune forme plurielle n'existe ; sans `count` numérique, `a.b_other` sert de
 * repli. Plus de « don(s) » à l'écran (tournage du 11/10/2026).
 */
const REGLES_PLURIEL: Record<Locale, Intl.PluralRules> = {
  fr: new Intl.PluralRules("fr"),
  en: new Intl.PluralRules("en"),
};

/**
 * Accord en genre, convention i18next `context` : `t("a.b", { context:
 * "feminin" })` lit `a.b_feminin` (et ses pluriels `a.b_feminin_one`…) quand
 * elle existe, sinon la clé simple `a.b`, qui porte la tournure NEUTRE, sans
 * parenthèses. Le contexte vient du sexe connu de la personne (voir
 * `lib/accord-genre.ts`) : plus de « Paroissien(ne) actif(ve) » ni de
 * « Marié(e) » sur la fiche d'une paroissienne (tournage du 11/10/2026).
 */
function resoudreCle(
  dict: TranslationDict,
  locale: Locale,
  key: string,
  params?: Record<string, string | number>,
): string | undefined {
  const contexte = params?.context;
  if (typeof contexte === "string" && contexte) {
    const accordee = resoudreSansContexte(dict, locale, `${key}_${contexte}`, params);
    if (accordee !== undefined) return accordee;
  }
  return resoudreSansContexte(dict, locale, key, params);
}

function resoudreSansContexte(
  dict: TranslationDict,
  locale: Locale,
  key: string,
  params?: Record<string, string | number>,
): string | undefined {
  const count = params?.count;
  if (typeof count === "number" && Number.isFinite(count)) {
    const forme = REGLES_PLURIEL[locale].select(count);
    const plurielle =
      getNestedValue(dict, `${key}_${forme}`) ?? getNestedValue(dict, `${key}_other`);
    if (plurielle !== undefined) return plurielle;
  }
  return getNestedValue(dict, key) ?? getNestedValue(dict, `${key}_other`);
}

// Note: aucun HTML escape ici. React échappe automatiquement les text nodes
// JSX (`{t(...)}`). Faire un escape ici causerait un double-escape visible
// (ex: "Père d'Aubigny" → "Père d&#39;Aubigny").
// RÈGLE DE SÉCURITÉ : ne JAMAIS passer le retour de `t()` à
// `dangerouslySetInnerHTML` ni à `.innerHTML` — un lint ESLint custom le
// garantit (voir .eslintrc).
function formatString(
  template: string,
  params?: Record<string, string | number>,
): string {
  if (!params) return template;
  return template.replace(/\{\{(\w+)\}\}(\.?)/g, (_, k: string, point: string) => {
    if (params[k] === undefined) return `{{${k}}}${point}`;
    const valeur = String(params[k]);
    // Typographie : le point d'une abréviation (« 04 oct. ») absorbe le point
    // final de la phrase, « au 04 oct. » et non « au 04 oct.. ». Les mois
    // sans abréviation (« mai », « juin », « août ») gardent leur point final.
    return point && valeur.endsWith(".") ? valeur : valeur + point;
  });
}

const I18nContext = createContext<I18nContextValue | null>(null);

interface I18nProviderProps {
  children: ReactNode;
  forcedLocale?: Locale;
}

export function I18nProvider({ children, forcedLocale }: I18nProviderProps) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    if (forcedLocale) return forcedLocale;
    try {
      const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
      if (stored && SUPPORTED_LOCALES.includes(stored as Locale)) {
        return stored as Locale;
      }
    } catch {
      /* SSR or storage disabled */
    }
    return DEFAULT_LOCALE;
  });
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    setIsReady(true);
  }, []);

  const setLocale = useCallback((newLocale: Locale) => {
    if (!SUPPORTED_LOCALES.includes(newLocale)) return;
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, newLocale);
    } catch {
      /* ignore */
    }
    setLocaleState(newLocale);
  }, []);

  const t = useCallback(
    (key: string, params?: Record<string, string | number>): string => {
      const dict = TRANSLATIONS[locale];
      const value = resoudreCle(dict, locale, key, params);
      if (value === undefined) return key; // fallback: clé brute
      return formatString(value, params);
    },
    [locale],
  );

  return (
    <I18nContext.Provider value={{ locale, setLocale, t, isReady }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error("useI18n must be used within an I18nProvider");
  }
  return ctx;
}

export function useTranslation(): (
  key: string,
  params?: Record<string, string | number>,
) => string {
  return useI18n().t;
}
